/**
 * Site Setup — PanelSiteService 연결
 */
import { createPanelSiteService } from '../services/panelSiteService';
import {
  panelSiteSidebarResizePlugin,
  readPanelSiteSidebarWidth,
  PANEL_SITE_SIDEBAR_DEFAULT_WIDTH
} from '@/ui/plugins';
import { PANEL_MAP_FLY_DURATION_MS } from '../utils/panelMapMotion.js';
import {
  panelPerfMark,
  panelPerfMeasure,
  panelPerfAsync,
  panelPerfSync,
  panelPerfCountSummary,
  panelPerfLogTileStats
} from '../utils/panelPlanningPerfLog.js';
import { clearPanelFlattenedRegistry } from '../utils/panelFlattenedRegistry.js';
import { clearPanelTreeModuleChildrenIndex } from '../utils/panelTreeModuleChildrenIndex.js';
import { clearPanelTreeLocalRegistry } from '../utils/panelTreeLocalRegistry.js';

export const siteSetup = {
  data() {
    return {
      siteListDrawer: true,
      siteSidebarWidth: PANEL_SITE_SIDEBAR_DEFAULT_WIDTH,
      selectedSite: null,
      siteSectionId: null,
      sites: [],
      siteSearchQuery: '',
      siteListFocusIndex: -1
    };
  },

  computed: {
    _siteSvc() { return createPanelSiteService(); },
    filteredSites() {
      return this._siteSvc.filterSites(this.sites, this.siteSearchQuery);
    },
    hasActiveSiteSearch() {
      return (this.siteSearchQuery || '').trim().length > 0 && this.filteredSites.length > 0;
    },
    siteListKeyboardHint() {
      if (!this.filteredSites?.length) return '';
      return this.$t('panelPlanning.siteSearchKeyboardHint');
    }
  },

  watch: {
    siteListDrawer(isOpen) {
      if (isOpen) this.$nextTick(() => this.initSiteSidebarResize());
    },
    siteSearchQuery() { this.syncSiteListFocusToResults(); },
    filteredSites() { this.syncSiteListFocusToResults(); }
  },

  methods: {
    async initSiteSidebarResize() {
      const el = this.$refs.siteSidebar;
      if (!el) return;
      this.siteSidebarWidth = await readPanelSiteSidebarWidth();
      this.teardownSiteSidebarResize();
      this._siteSidebarResizeCleanup = panelSiteSidebarResizePlugin.attach(el, {
        getWidth: () => this.siteSidebarWidth,
        setWidth: w => { this.siteSidebarWidth = w; },
        onResize: () => { this.map?.updateSize(); }
      });
    },

    teardownSiteSidebarResize() {
      this._siteSidebarResizeCleanup?.();
      this._siteSidebarResizeCleanup = null;
    },

    async fetchSitesFromServer() {
      const sites = await panelPerfAsync('fetchSitesFromServer', () => this._siteSvc.fetchSites());
      this.sites = sites;
      panelPerfCountSummary('fetchSitesFromServer:count', { sites: sites?.length ?? 0 });
    },

    mergeSelectedSiteMetadata(site) {
      if (!site?.id) return;
      const hadLocation = !!this.selectedSite?.location?.coordinates?.length;
      this.selectedSite = { ...this.selectedSite, ...site };
      if (!hadLocation && site.location?.coordinates?.length) {
        this.flyToCoordinates(site.location.coordinates, () => {});
      }
    },

    /**
     * 마운트 시 sites 목록과 site-design 로드를 병렬화.
     * localStorage에 저장된 site id가 있으면 목록 응답을 기다리지 않고 design 로드를 시작한다.
     */
    async bootstrapSitesAndDesignOnMount() {
      panelPerfMark('bootstrapSitesAndDesignOnMount');
      const savedId = localStorage.getItem('solvit-panel-site-id');
      panelPerfCountSummary('bootstrap:savedSiteId', { savedId: savedId || null });

      const designLoadPromise = savedId
        ? this.selectSiteAndLoadDesign({ id: savedId })
        : null;

      await this.fetchSitesFromServer();

      if (!this.sites?.length) {
        panelPerfMeasure('bootstrapSitesAndDesignOnMount:empty', 'bootstrapSitesAndDesignOnMount');
        return;
      }

      const resolvedSaved = savedId
        ? this.sites.find(s => String(s.id) === String(savedId))
        : null;

      if (resolvedSaved && String(this.selectedSite?.id) === String(savedId)) {
        this.mergeSelectedSiteMetadata(resolvedSaved);
        panelPerfMeasure('bootstrapSitesAndDesignOnMount:metadataOnly', 'bootstrapSitesAndDesignOnMount');
        if (designLoadPromise) {
          panelPerfMark('bootstrap:awaitDesignLoad:start');
          await designLoadPromise;
          panelPerfMeasure('bootstrap:awaitDesignLoad', 'bootstrap:awaitDesignLoad:start');
        }
        return;
      }

      if (!designLoadPromise) {
        await this.selectSiteAndLoadDesign(resolvedSaved || this.sites[0]);
      }

      panelPerfMeasure('bootstrapSitesAndDesignOnMount:done', 'bootstrapSitesAndDesignOnMount');
      panelPerfLogTileStats('bootstrap complete');
    },

    async selectSiteAndLoadDesign(site) {
      panelPerfMark('selectSiteAndLoadDesign', { siteId: site?.id, siteName: site?.name });
      this.cancelActiveMapInteractions?.();
      clearPanelFlattenedRegistry();
      clearPanelTreeModuleChildrenIndex();
      clearPanelTreeLocalRegistry();
      this.moduleTreeCommitPending = false;
      this.selectedSite = site;
      this._siteSvc.persistSelectedSiteId(site.id);
      this.flyToCoordinates(site.location?.coordinates, () => {}, 'selectSite-flyTo');
      try {
        await this.runWithModuleTreeLoading(async () => {
          const loadResult = await panelPerfAsync('loadSiteDesign', () =>
            this._siteSvc.loadSiteDesign(site.id)
          );
          const { flattened, sectionId } = loadResult;
          this.flattenedSiteDesign = flattened;
          this.siteSectionId = sectionId;
          this._lastSyncedFlattenedSiteDesignRef = null;
          this.syncFlattenedToStore?.();

          panelPerfSync('drawSiteDesignOnMap', () => {
            this.drawSiteDesignOnMap(flattened);
          });

          panelPerfSync('generateTreeDataFromSiteHandler', () => {
            this.generateTreeDataFromSiteHandler?.({
              selectedSite: site,
              solarStrings: this.solarStrings
            });
          });

          this.$nextTick(() => {
            this.applyInitialHierarchySelection?.();
          });
        });
        panelPerfMeasure('selectSiteAndLoadDesign:done', 'selectSiteAndLoadDesign');
        panelPerfLogTileStats('selectSiteAndLoadDesign complete');
      } catch (e) {
        panelPerfMeasure('selectSiteAndLoadDesign:error', 'selectSiteAndLoadDesign', e?.message);
        console.error('site load fail:', e);
      }
    },

    selectFirstFilteredSite() {
      const s = this.filteredSites;
      if (!s?.length) return;
      this.selectSiteAndLoadDesign(s[this.siteListFocusIndex >= 0 ? this.siteListFocusIndex : 0]);
    },

    syncSiteListFocusToResults() {
      this.siteListFocusIndex = this._siteSvc.clampFocusIndex(this.filteredSites.length, this.siteListFocusIndex);
    },

    onSiteSearchKeydown(event) {
      const list = this.filteredSites;
      if (!list?.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.siteListFocusIndex = this._siteSvc.navigateFocusIndex(this.siteListFocusIndex, list.length, 1);
        this.scrollSiteListFocusIntoView();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.siteListFocusIndex = this._siteSvc.navigateFocusIndex(this.siteListFocusIndex, list.length, -1);
        this.scrollSiteListFocusIntoView();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        this.selectSiteAndLoadDesign(list[this.siteListFocusIndex >= 0 ? this.siteListFocusIndex : 0]);
      }
    },

    onSiteListItemClick(site, index) {
      this.siteListFocusIndex = index;
      this.selectSiteAndLoadDesign(site);
    },

    scrollSiteListFocusIntoView() {
      this.$nextTick(() => {
        const ref = this.$refs[`siteListItem_${this.siteListFocusIndex}`];
        const el = (Array.isArray(ref) ? ref[0] : ref)?.$el;
        el?.scrollIntoView({ block: 'nearest' });
      });
    },

    flyToCoordinates(location, done, reason = 'flyTo') {
      if (!location?.length || !this.view) {
        panelPerfCountSummary(`${reason}:skip`, { hasLocation: !!location?.length, hasView: !!this.view });
        done?.(false);
        return;
      }
      panelPerfMark(`${reason}:start`, { center: location, zoom: this.view.getZoom() });
      const dur = PANEL_MAP_FLY_DURATION_MS;
      const zoom = this.view.getZoom();
      let parts = 2;
      let called = false;
      const cb = c => {
        --parts;
        if (called) return;
        if (parts === 0 || !c) {
          called = true;
          panelPerfMeasure(`${reason}:done`, `${reason}:start`);
          panelPerfLogTileStats(`${reason} animation end`);
          done?.(c);
        }
      };
      this.view.animate({ center: location, duration: dur }, cb);
      this.view.animate({ zoom: zoom - 1, duration: dur / 2 }, { zoom, duration: dur / 2 }, cb);
    }
  }
};
